import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useScroll, useMotionValueEvent } from 'framer-motion';

/**
 * ShrinkingScrollExit 컴포넌트
 * 스크롤 위치에 따라 요소가 bottom에서 등장하고 top으로 사라지는 전체 생명주기 애니메이션
 * 
 * 생명주기:
 * 1. Bottom FadeIn: 아래에서 등장 (targetScale/targetOpacity → 1/1)
 * 2. Normal State: 정상 표시 (1/1) 
 * 3. Top FadeOut: 위로 사라짐 (1/1 → targetScale/targetOpacity)
 * 
 * Props:
 * @param {React.ReactNode} children - 애니메이션을 적용할 자식 요소들 [Required]
 * @param {number} triggerTop - 위쪽 사라짐 시작 위치 (뷰포트 상단 기준, px) [Optional, 기본값: 320]
 * @param {number} triggerBottom - 아래쪽 등장 시작 위치 (뷰포트 하단 기준, px) [Optional, 기본값: 240]
 * @param {number} fadeInStartBottom - 페이드인 시작하는 bottom 위치 (이 값보다 낮을때는 target값 유지, px) [Optional, 기본값: 0]
 * @param {number} targetScale - 축소된 상태의 스케일 값 (0~1) [Optional, 기본값: 0.5]
 * @param {number} targetY - 축소된 상태의 Y 이동 거리(px) [Optional, 기본값: 0]
 * @param {number} targetOpacity - 축소된 상태의 투명도 (0~1) [Optional, 기본값: 0.3]
 * @param {boolean} enableOpacityMapping - 투명도 매핑 활성화 여부 [Optional, 기본값: true]
 * @param {'left'|'center'|'right'} textAlign - 텍스트 정렬 및 축소 기준점 [Optional, 기본값: 'center']
 * @param {string} transformOrigin - 커스텀 축소 기준점 [Optional]
 * @param {object} sx - 추가 스타일 객체 [Optional]
 *
 * Example usage:
 * <ShrinkingScrollExit 
 *   triggerTop={320}
 *   triggerBottom={240}
 *   targetScale={0.5}
 *   targetOpacity={0.3}
 *   textAlign="center"
 * >
 *   <Typography variant="h1">Full Lifecycle Content</Typography>
 * </ShrinkingScrollExit>
 */
function ShrinkingScrollExit({
  children,
  triggerTop = 320,
  triggerBottom = 240,
  fadeInStartBottom = 0,
  targetScale = 0.5,
  targetY = 0,
  targetOpacity = 0.3,
  enableOpacityMapping = true,
  textAlign = 'center',
  transformOrigin = null,
  sx = {},
}) {
  const componentRef = useRef(null);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  
  // Transform origin 설정
  const finalTransformOrigin = transformOrigin || `${textAlign} center`;
  
  const [animationValues, setAnimationValues] = useState({
    scale: 1,
    y: 0,
    opacity: 1,
  });

  // framer-motion 스크롤 감지
  const { scrollY } = useScroll();

  // 애니메이션 계산 함수 - 전체 생명주기 처리
  const calculateAnimationValues = (rect) => {
    const elementTop = rect.top;
    const elementBottom = rect.bottom;
    const viewportHeight = window.innerHeight;
    
    // Stage 3: Top FadeOut (elementTop < triggerTop)
    if (elementTop < triggerTop) {
      const progress = Math.min(Math.max((triggerTop - elementTop) / triggerTop, 0), 1);
      const scale = 1 + (targetScale - 1) * progress;
      const y = targetY * progress;
      const opacity = enableOpacityMapping ? 1 + (targetOpacity - 1) * progress : 1;
      

      
      return { scale, y, opacity };
    }
    
    // Stage 1-1: 페이드인 시작 전 (elementBottom > viewportHeight - fadeInStartBottom)
    const fadeInTriggerPoint = viewportHeight - fadeInStartBottom;
    if (fadeInStartBottom > 0 && elementBottom > fadeInTriggerPoint) {
      // fadeInStartBottom 값보다 낮을 때는 target 값 유지
      return { scale: targetScale, y: targetY, opacity: enableOpacityMapping ? targetOpacity : 1 };
    }
    
    // Stage 1-2: Bottom FadeIn (elementBottom > viewportHeight - triggerBottom)
    const bottomTriggerPoint = viewportHeight - triggerBottom;
    if (elementBottom > bottomTriggerPoint) {
      const progress = Math.min(Math.max((bottomTriggerPoint + triggerBottom - elementBottom) / triggerBottom, 0), 1);
      const scale = targetScale + (1 - targetScale) * progress;
      const y = targetY + (0 - targetY) * progress;
      const opacity = enableOpacityMapping ? targetOpacity + (1 - targetOpacity) * progress : 1;
      
      return { scale, y, opacity };
    }
    
    // Stage 2: Normal State (중간 구간)
    return { scale: 1, y: 0, opacity: 1 };
  };

  // 스크롤 이벤트 감지 및 애니메이션 값 업데이트
  useMotionValueEvent(scrollY, 'change', () => {
    if (!componentRef.current) return;

    const rect = componentRef.current.getBoundingClientRect();
    const newValues = calculateAnimationValues(rect);
    setAnimationValues(newValues);
  });

  // 초기 위치 확인 (컴포넌트 마운트 시)
  useEffect(() => {
    if (!componentRef.current) return;

    const rect = componentRef.current.getBoundingClientRect();
    const initialValues = calculateAnimationValues(rect);
    setAnimationValues(initialValues);

    // 리사이즈 시에도 위치 재계산
    const handleResize = () => {
      if (!componentRef.current) return;
      const rect = componentRef.current.getBoundingClientRect();
      const newValues = calculateAnimationValues(rect);
      setAnimationValues(newValues);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [triggerTop, triggerBottom, fadeInStartBottom, targetScale, targetY, targetOpacity, enableOpacityMapping]);

  return (
    <Box
      ref={componentRef}
      data-shrinking-scroll-instance={instanceId.current}
      sx={{
        transformOrigin: finalTransformOrigin,
        willChange: 'transform, opacity',
        transform: `scale(${animationValues.scale}) translateY(${animationValues.y}px)`,
        opacity: animationValues.opacity,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

ShrinkingScrollExit.propTypes = {
  children: PropTypes.node.isRequired,
  triggerTop: PropTypes.number,
  triggerBottom: PropTypes.number,
  fadeInStartBottom: PropTypes.number,
  targetScale: PropTypes.number,
  targetY: PropTypes.number,
  targetOpacity: PropTypes.number,
  enableOpacityMapping: PropTypes.bool,
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  transformOrigin: PropTypes.string,
  sx: PropTypes.object,
};

export default ShrinkingScrollExit; 